import { Drawer } from '@metadevprofile/ui/web';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/common/hooks/use-mobile';
import { cn } from '@/common/lib/utils';

const SIDEBAR_STORAGE_KEY = 'sidebar-open';

type SidebarContextValue = {
  open: boolean;
  openMobile: boolean;
  isMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  toggleSidebar: () => void;
};

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }
  return context;
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored !== null) setOpen(stored === 'true');
  }, []);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setOpenMobile((value) => !value);
      return;
    }
    setOpen((value) => {
      const next = !value;
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      return next;
    });
  }, [isMobile]);

  const value = useMemo<SidebarContextValue>(
    () => ({ open, openMobile, isMobile, setOpenMobile, toggleSidebar }),
    [open, openMobile, isMobile, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={value}>
      <div className="flex h-svh w-full overflow-hidden">{children}</div>
    </SidebarContext.Provider>
  );
}

export function Sidebar({ className, children }: { className?: string; children: React.ReactNode }) {
  const { open, isMobile, openMobile, setOpenMobile } = useSidebar();
  const { t } = useTranslation('common');

  if (isMobile) {
    return (
      <Drawer.Backdrop isOpen={openMobile} onOpenChange={setOpenMobile}>
        <Drawer.Content placement="left">
          <Drawer.Dialog
            aria-label={t('ui.sidebar.title')}
            className={cn('flex h-full w-64 flex-col bg-surface p-0', className)}
          >
            <Drawer.CloseTrigger />
            {children}
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    );
  }

  return (
    <aside
      data-state={open ? 'expanded' : 'collapsed'}
      className={cn(
        'hidden h-full shrink-0 flex-col overflow-hidden bg-surface transition-[width] duration-200 ease-linear md:flex',
        open ? 'w-64' : 'w-0',
        className,
      )}
    >
      <div className="flex h-full w-64 flex-col">{children}</div>
    </aside>
  );
}

export function SidebarHeader({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('flex flex-col gap-2 p-2', className)}>{children}</div>;
}

export function SidebarFooter({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('flex flex-col gap-2 p-2', className)}>{children}</div>;
}

export function SidebarContent({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('flex min-h-0 flex-1 flex-col gap-2 overflow-auto', className)}>{children}</div>;
}

export function SidebarGroup({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('flex w-full min-w-0 flex-col p-2', className)}>{children}</div>;
}

export function SidebarGroupLabel({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div className={cn('flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium', className)}>
      {children}
    </div>
  );
}

export function SidebarGroupContent({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('w-full text-sm', className)}>{children}</div>;
}

export function SidebarMenu({ className, children }: { className?: string; children: React.ReactNode }) {
  return <ul className={cn('flex w-full min-w-0 flex-col gap-1', className)}>{children}</ul>;
}

export function SidebarMenuItem({ className, children }: { className?: string; children: React.ReactNode }) {
  return <li className={cn('relative', className)}>{children}</li>;
}
